import {
    Add,
    AlertModal,
    Box,
    Button,
    Copy,
    Delete,
    Divider,
    Modal,
    MultiSelect,
    Pagination,
    SearchBar,
    SearchSelect,
    Select,
    showToast,
    SubPageLayout,
    TextInput,
    Upload,
} from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import ApplicationLayout from "components/layout/ApplicationLayout";
import { RULES_ROUTE } from "components/router/Routes";
import style from "components/rules/deviceStatus.scss";
import { parseDeviceStatusImport } from "components/rules/deviceStatusImport";
import { DeviceStatusRow } from "components/rules/deviceStatusRow";
import { EditableRulesTable } from "components/rules/EditableRulesTable";
import { ImportDeviceValueRulesModal } from "components/rules/ImportDeviceValueRulesModal";
import {
    applyPendingChanges,
    collectPendingChanges,
    hasPendingChanges,
    NO_PENDING_CHANGES,
    PendingRuleChanges,
    toRules,
} from "components/rules/pendingRuleChanges";
import { useEditableRows } from "components/rules/useEditableRows";
import { TableFooter } from "components/table/TableFooter";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { GRADES, isGrade } from "domain/grade";
import { UpliftRule } from "domain/upliftRules";
import {
    PricingRule,
    PricingRuleAction,
    PricingRuleData,
    PricingRuleErrorResponse,
    PricingRuleKeysResult,
    pricingRuleService,
    validatePricingRules,
} from "services/asset-management/pricing-rules/PricingRuleService";
import { upliftRulesService } from "services/asset-management/uplift-rules/UpliftRulesService";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

type TextField = "manufacturer" | "model" | "blanccoGrade" | "value";

// The rules of the page that is shown, as the server returned them, and the size of the full result.
interface ServerPage {
    rules: DeviceStatusRow[];
    totalPages: number;
    totalRules: number;
}

const deviceStatusRowIds = testIds.workArea.deviceStatus.table.ruleRow;

// Number of rules fetched from the API per request.
const PAGE_SIZE = 100;
const MAX_PAGE_NUMBERS_TO_SHOW = 5;

const DEFAULT_CURRENCY = "USD";

const CURRENCY_SYMBOL = "$";

const formatValue = (value: string) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return value;
    }
    return new Intl.NumberFormat("en-US", { style: "currency", currency: DEFAULT_CURRENCY }).format(amount);
};

const NO_GRADE = "NO_GRADE";

const isRowComplete = (row: DeviceStatusRow) =>
    [row.manufacturer, row.model, row.value].every((field) => field && field.trim() !== "");

const trimRow = (row: DeviceStatusRow) => ({
    ...row,
    manufacturer: row.manufacturer.trim(),
    model: row.model.trim(),
    value: row.value.trim(),
});

const toUpliftHeadlines = (row: DeviceStatusRow, headlineByUuid: Record<string, string>) =>
    row.upliftValues.map((uuid) => headlineByUuid[uuid]).filter((headline) => headline !== undefined);

const matchesQuery = (row: DeviceStatusRow, headlineByUuid: Record<string, string>, query?: string) => {
    if (!query) {
        return true;
    }
    const haystack = [row.manufacturer, row.model, row.blanccoGrade, ...toUpliftHeadlines(row, headlineByUuid)]
        .join(" ")
        .toLowerCase();
    return haystack.includes(query.toLowerCase());
};

interface RuleChangeListProps {
    title: string;
    rules: PricingRule[];
}

const RuleChangeList = (props: RuleChangeListProps) => {
    if (props.rules.length === 0) {
        return null;
    }
    const describeRule = (rule: PricingRule) =>
        [rule.manufacturer, rule.model, rule.blanccoGrade, formatValue(String(rule.value))]
            .filter((part) => part)
            .join(" - ");
    return (
        <>
            <TextBlock disableBottomSpacing>{props.title}</TextBlock>
            <ul>
                {props.rules.map((rule, index) => (
                    <li key={rule.uuid ?? index}>{describeRule(rule)}</li>
                ))}
            </ul>
        </>
    );
};

const DeviceStatusContent = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const editableRows = useEditableRows<DeviceStatusRow>({
        createEmptyRow: (id) => ({
            id,
            manufacturer: "",
            model: "",
            value: "",
            upliftValues: [],
            isEditing: true,
            isCreated: false,
            validated: false,
        }),
        isRowComplete,
        trimRow,
    });
    const rows = editableRows.rows;
    const selectedIds = editableRows.selectedIds;
    const [serverPage, setServerPage] = React.useState<ServerPage>({ rules: [], totalPages: 1, totalRules: 0 });
    // Unsaved changes, except for the new rules that are on screen. Those are in the table rows.
    const [pending, setPending] = React.useState<PendingRuleChanges>(NO_PENDING_CHANGES);
    const [isLoadingPage, setIsLoadingPage] = React.useState(true);
    const abortControllerRef = React.useRef<AbortController | undefined>(undefined);
    const [searchQuery, setSearchQuery] = React.useState<string>();
    const [currentPage, setCurrentPage] = React.useState(1);
    const [isCancelDialogVisible, setIsCancelDialogVisible] = React.useState(false);
    const [isErrorModalVisible, setIsErrorModalVisible] = React.useState(false);
    const [isConfirmSaveVisible, setIsConfirmSaveVisible] = React.useState(false);
    const [isImportModalVisible, setIsImportModalVisible] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [batchError, setBatchError] = React.useState<string | null>(null);
    const [upliftRules, setUpliftRules] = React.useState<UpliftRule[]>([]);
    const [upliftRuleItems, setUpliftRuleItems] = React.useState<{ value: string; label: string }[]>([]);
    const [selectedUpliftUuids, setSelectedUpliftUuids] = React.useState<string[]>([]);
    const [availableKeys, setAvailableKeys] = React.useState<PricingRuleKeysResult>();

    const headlineByUuid = React.useMemo(
        () => Object.fromEntries(upliftRules.map((rule) => [rule.uuid, rule.headline])),
        [upliftRules]
    );

    const toRow = (rule: PricingRuleData): DeviceStatusRow => ({
        id: editableRows.createRowId(),
        uuid: rule.uuid,
        manufacturer: rule.manufacturer,
        model: rule.model,
        blanccoGrade: rule.blanccoGrade,
        value: String(rule.value),
        upliftValues: rule.upliftValues ?? [],
        isEditing: false,
        isCreated: false,
        validated: true,
    });

    const showPage = (savedRows: DeviceStatusRow[], changes: PendingRuleChanges, page: number, search?: string) => {
        const createdOnPage =
            page === 1 ? changes.created.filter((row) => matchesQuery(row, headlineByUuid, search)) : [];
        setPending({ ...changes, created: changes.created.filter((row) => !createdOnPage.includes(row)) });
        editableRows.replaceRows([...createdOnPage, ...applyPendingChanges(savedRows, changes)]);
    };

    const loadPage = (page: number, search: string | undefined, changes: PendingRuleChanges): Promise<void> => {
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsLoadingPage(true);
        return pricingRuleService
            .fetchRulesPage(page, PAGE_SIZE, search, abortController)
            .then((result) => {
                if (abortController.signal.aborted) {
                    return;
                }
                // Deleting rules can leave the requested page past the end.
                if (result.rules.length === 0 && result.totalPages > 0 && page > result.totalPages) {
                    return loadPage(result.totalPages, search, changes);
                }
                const savedRows = result.rules.map(toRow);
                setServerPage({
                    rules: savedRows,
                    totalPages: Math.max(1, result.totalPages),
                    totalRules: result.totalRules,
                });
                setCurrentPage(page);
                setSearchQuery(search);
                showPage(savedRows, changes, page, search);
                setIsLoadingPage(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to load pricing rules.");
                    setIsLoadingPage(false);
                }
            });
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        loadPage(1, undefined, NO_PENDING_CHANGES);
        upliftRulesService
            .fetchAllUpliftRules(abortController)
            .then((rules) => {
                setUpliftRules(rules);
                setUpliftRuleItems(rules.map((rule) => ({ value: rule.uuid, label: rule.headline })));
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    console.error("Failed to load uplift rules.", error);
                }
            });
        return () => {
            abortController.abort();
            abortControllerRef.current?.abort();
        };
    }, []);

    React.useEffect(() => {
        const abortController = new AbortController();
        pricingRuleService
            .fetchExistingKeysForRules(abortController)
            .then((result) => setAvailableKeys(result))
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to load existing models and manufacturers.", error);
                }
            });
        return () => abortController.abort();
    }, []);

    const allUpliftValuesSelectedText = upliftRuleItems.length > 0 ? t("DeviceStatus.allUpliftValuesSelected") : "";
    const hasSelection = selectedIds.length > 0;
    const hasEditingRow = rows.some((row) => row.isEditing);
    const mergedChanges = React.useMemo(
        () => collectPendingChanges(pending, rows, serverPage.rules),
        [pending, rows, serverPage.rules]
    );
    const hasChanges = hasPendingChanges(mergedChanges);
    const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
    const savedRowCountOnPage = rows.filter((row) => row.uuid).length;

    const changeView = (page: number, search: string | undefined) => {
        if (isLoadingPage) {
            return;
        }
        if (hasEditingRow) {
            showToast(t("DeviceStatus.confirmEditsBeforeNavigating"), "info");
            return;
        }
        loadPage(page, search, mergedChanges);
    };

    const discardChanges = () => showPage(serverPage.rules, NO_PENDING_CHANGES, currentPage, searchQuery);

    const addRow = () => editableRows.addRow();

    const duplicateSelectedRows = () => {
        // The copies are new rules, so they must not refer to the original rules on the server.
        editableRows.duplicateRows(selectedIds, (row) => ({ ...row, uuid: undefined }));
    };

    const handleImport = (records: Record<string, string>[]) => {
        setIsImportModalVisible(false);
        const uuidByHeadline = Object.fromEntries(upliftRules.map((rule) => [rule.headline.toLowerCase(), rule.uuid]));
        const result = parseDeviceStatusImport(records, uuidByHeadline);
        const importedRows = result.rows.map((row) => ({ ...row, id: editableRows.createRowId() }));
        loadPage(1, undefined, { ...mergedChanges, created: [...mergedChanges.created, ...importedRows] });
        if (result.invalidRowCount > 0) {
            showToast(t("DeviceStatus.import.invalidRows", { count: result.invalidRowCount }), "info");
        }
    };

    const pendingRules = toRules(mergedChanges);
    const pendingRulesByAction = (action: PricingRuleAction) => pendingRules.filter((rule) => rule.action === action);

    const extractErrorMessage = (message: string): string => {
        const match = message.match(/rules\[\d+\]:\s*(.+)/);
        const extractedMessage = match ? match[1] : message;
        return extractedMessage.charAt(0).toUpperCase() + extractedMessage.slice(1);
    };

    const handleSave = () => {
        if (hasEditingRow) {
            return;
        }
        const validation = validatePricingRules(pendingRules.filter((rule) => rule.action !== "DELETE"));
        if (!validation.valid) {
            setBatchError(validation.errors.map((error) => extractErrorMessage(error.message)).join("; "));
            setIsErrorModalVisible(true);
            return;
        }
        setBatchError(null);
        setIsSaving(true);
        pricingRuleService
            .applyRules(pendingRules)
            .then(() => {
                showToast(t("AssetManagement.value.deviceValueSettings.toast.saveSuccess"), "success");
                return loadPage(currentPage, searchQuery, NO_PENDING_CHANGES);
            })
            .catch((error: PricingRuleErrorResponse | Error) => {
                let errorMessage = "";

                if ("errors" in error && Array.isArray(error.errors)) {
                    errorMessage = error.errors
                        .map((singleError) => extractErrorMessage(singleError.message))
                        .join("; ");
                } else {
                    const rawError =
                        (error instanceof Error ? error.message : null) ||
                        t("AssetManagement.value.deviceValueSettings.toast.saveError");
                    if (rawError === "Failed to fetch") {
                        errorMessage = t("AssetManagement.value.deviceValueSettings.modal.connectionError");
                    } else {
                        try {
                            const parsed = JSON.parse(rawError);
                            if (parsed.error?.message) {
                                errorMessage = extractErrorMessage(parsed.error.message);
                            } else if (parsed.errors && Array.isArray(parsed.errors)) {
                                errorMessage = parsed.errors
                                    .map((singleError: { message: string }) => extractErrorMessage(singleError.message))
                                    .join("; ");
                            } else {
                                errorMessage = extractErrorMessage(rawError);
                            }
                        } catch {
                            errorMessage = extractErrorMessage(rawError);
                        }
                    }
                }

                setBatchError(errorMessage);
                setIsErrorModalVisible(true);
            })
            .finally(() => setIsSaving(false));
    };

    const handleDontSave = () => {
        discardChanges();
        setIsCancelDialogVisible(false);
    };

    const renderCells = (row: DeviceStatusRow) => {
        if (!row.isEditing) {
            const textCell = (key: string, text: string) => (
                <span key={key} className={style.truncatedItem}>
                    {text}
                </span>
            );
            const upliftHeadlines = toUpliftHeadlines(row, headlineByUuid).join(", ");
            return [
                textCell("manufacturer", row.manufacturer),
                textCell("model", row.model),
                textCell("blanccoGrade", row.blanccoGrade ?? t("DeviceStatus.noGrade")),
                textCell("value", formatValue(row.value)),
                textCell("uplift", upliftHeadlines),
            ];
        }

        const searchSelect = (
            field: "manufacturer" | "model",
            items: string[],
            testId: { itself: string; errorLabel: string }
        ) => (
            <SearchSelect
                key={field}
                allowCreateOption
                closeOnSelection
                createOptionText={t("DeviceStatus.createOption")}
                emptyStateText={t("DeviceStatus.noResults")}
                items={items}
                name={`device-status-${field}-${row.id}`}
                onCreateOption={(created) => editableRows.updateRow(row.id, field, created.trim())}
                onSelection={(selected) => editableRows.updateRow(row.id, field, String(selected))}
                placeholder={t(`DeviceStatus.${field}`)}
                searchPlaceholder={t("DeviceStatus.search")}
                selectedByDefault={row[field] || undefined}
                hideLabelSection={true}
                testIds={{ input: testId.itself, error: testId.errorLabel }}
                error={!row[field].trim() && row.validated ? t("DeviceStatus.fieldRequired") : undefined}
                dropdownMaxHeight={400}
                flipOptions={{ fallbackPlacements: ["top-start"] }}
            />
        );

        const textInput = (
            field: TextField,
            testId: { itself: string; errorLabel: string },
            leftSection?: React.ReactNode
        ) => {
            const isMandatory = field === "manufacturer" || field === "model" || field === "value";
            const isEmpty = !row[field] || (row[field] as string).trim() === "";
            return (
                <TextInput
                    key={field}
                    name={`device-status-${field}-${row.id}`}
                    placeholder={t(`DeviceStatus.${field}`)}
                    value={row[field]}
                    onChange={(event) => editableRows.updateRow(row.id, field, event.target.value)}
                    hideLabelSection={true}
                    testIds={{ input: testId.itself, error: testId.errorLabel }}
                    leftSection={leftSection}
                    error={isMandatory && isEmpty && row.validated ? t("DeviceStatus.fieldRequired") : undefined}
                />
            );
        };

        const keyInput = (
            field: "manufacturer" | "model",
            items: string[] | undefined,
            testId: { itself: string; errorLabel: string }
        ) => (items?.length ? searchSelect(field, items, testId) : textInput(field, testId));

        return [
            keyInput("manufacturer", availableKeys?.manufacturers, deviceStatusRowIds.manufacturerInput),
            keyInput("model", availableKeys?.models, deviceStatusRowIds.modelInput),
            <Select
                key="blanccoGrade"
                name={`device-status-grade-${row.id}`}
                placeholder={t("DeviceStatus.grade")}
                items={[
                    { value: NO_GRADE, label: t("DeviceStatus.noGrade") },
                    ...GRADES.map((grade) => ({ value: grade, label: grade })),
                ]}
                selectedByDefault={row.blanccoGrade ?? NO_GRADE}
                onSelection={(selected) =>
                    editableRows.updateRow(row.id, "blanccoGrade", isGrade(selected) ? selected : undefined)
                }
                hideLabelSection={true}
                closeOnSelection={true}
                testIds={{ input: deviceStatusRowIds.gradeSelect }}
            />,
            textInput("value", deviceStatusRowIds.valueInput, CURRENCY_SYMBOL),
            <MultiSelect
                key="uplift"
                values={upliftRuleItems}
                preselectedValues={upliftRuleItems.filter((item) => row.upliftValues.includes(String(item.value)))}
                onSelectedChange={(selected) =>
                    editableRows.updateRow(
                        row.id,
                        "upliftValues",
                        selected.map((selectedValue) =>
                            String(typeof selectedValue === "object" ? selectedValue.value : selectedValue)
                        )
                    )
                }
                selectAllByDefault={false}
                allSelectedText={allUpliftValuesSelectedText}
                multiselectText={t("DeviceStatus.upliftValuesSelected")}
                noneSelectedText={t("DeviceStatus.selectUpliftValues")}
                selectAllCheckboxLabel={t("DeviceStatus.selectAllUpliftValues")}
                testId={deviceStatusRowIds.upliftSelect}
            />,
        ];
    };

    return (
        <>
            <SubPageLayout
                className={style.rulesSubPageLayout}
                title={""}
                cancelButtonLabel={t("Common.cancel")}
                handleClose={() => setIsCancelDialogVisible(true)}
                testIds={{ cancelButton: testIds.workArea.deviceStatus.cancelButton }}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => setIsConfirmSaveVisible(true)}
                            disabled={!hasChanges || hasEditingRow || isSaving}
                            isLoading={isSaving}
                            testId={testIds.workArea.deviceStatus.saveButton}
                        >
                            {t("Common.save")}
                        </Button>
                    </>
                }
            >
                <Box columnLayout gap="Sm" className={style.content}>
                    <TextBlock disableBottomSpacing>{t("DeviceStatus.viewDescription")}</TextBlock>
                    <Box className={style.toolbar} gap="Sm">
                        <SearchBar
                            name="device-status-search"
                            placeholder={t("Common.search")}
                            onSearch={(query: string) => changeView(1, query.trim() || undefined)}
                            onClear={() => changeView(1, undefined)}
                        />
                        <Divider direction="Vertical" className={style.dividerHeight} />
                        <Button
                            variant="TertiaryDelete"
                            icon={<Delete />}
                            disabled={!hasSelection}
                            onClick={() => editableRows.deleteRows(selectedIds)}
                            testId={testIds.workArea.deviceStatus.deleteButton}
                        >
                            {t("Common.delete")}
                        </Button>
                        <Divider direction="Vertical" className={style.dividerHeight} />
                        <Button
                            variant="Tertiary"
                            icon={<Copy color={theme.iconFillColor} />}
                            disabled={!hasSelection}
                            onClick={duplicateSelectedRows}
                            testId={testIds.workArea.deviceStatus.duplicateButton}
                        >
                            {t("Common.duplicate")}
                        </Button>
                        <Divider direction="Vertical" className={style.dividerHeight} />
                        <span>{t("DeviceStatus.bulkUplift.setValues")}</span>
                        {upliftRuleItems && (
                            <MultiSelect
                                key={`bulk-uplift-${upliftRuleItems.length}`}
                                values={upliftRuleItems}
                                selectAllByDefault={false}
                                preselectedValues={upliftRuleItems.filter((item) =>
                                    selectedUpliftUuids.includes(item.value)
                                )}
                                searchable={true}
                                onSelectedChange={(selected) => {
                                    const uuids = selected.map((selectedValue) =>
                                        String(typeof selectedValue === "object" ? selectedValue.value : selectedValue)
                                    );
                                    setSelectedUpliftUuids(uuids);
                                    selectedIds.forEach((id) => editableRows.updateRow(id, "upliftValues", uuids));
                                }}
                                allSelectedText={allUpliftValuesSelectedText}
                                multiselectText={t("DeviceStatus.upliftValuesSelected")}
                                noneSelectedText={t("DeviceStatus.bulkUplift.selectPlaceholder")}
                                selectAllCheckboxLabel={t("DeviceStatus.selectAllUpliftValues")}
                                testId={testIds.workArea.deviceStatus.upliftSelect.itself}
                                className={!hasSelection ? style.disabledMultiSelect : undefined}
                            />
                        )}
                        <Divider direction="Vertical" className={style.dividerHeight} />
                        <Button
                            variant="Secondary"
                            icon={<Upload />}
                            onClick={() => setIsImportModalVisible(true)}
                            disabled={hasEditingRow}
                            testId={testIds.workArea.deviceStatus.importButton}
                        >
                            {t("Common.import")}
                        </Button>
                    </Box>
                    {!isLoadingPage && (rows.length > 0 || searchQuery !== undefined || serverPage.totalRules > 0) && (
                        <EditableRulesTable
                            name="device-status"
                            className={style.tableContainer}
                            testId={testIds.workArea.deviceStatus.table.itself}
                            rowTestIds={deviceStatusRowIds}
                            emptyStateMessage={
                                searchQuery === undefined
                                    ? t("DeviceStatus.allRulesOnPageRemoved")
                                    : t("DeviceStatus.emptySearchResult")
                            }
                            selectAllLabel={t("DeviceStatus.selectAll")}
                            headers={[
                                t("DeviceStatus.manufacturer"),
                                t("DeviceStatus.model"),
                                t("DeviceStatus.grade"),
                                t("DeviceStatus.value"),
                                t("DeviceStatus.upliftValue"),
                            ]}
                            rows={rows}
                            editableRows={editableRows}
                            isRowComplete={isRowComplete}
                            renderCells={renderCells}
                        />
                    )}
                    <Box marginTop="Sm" marginBottom="Sm">
                        <Button
                            variant="Tertiary"
                            icon={<Add />}
                            onClick={addRow}
                            testId={testIds.workArea.deviceStatus.addRuleButton}
                        >
                            {t("DeviceStatus.addRule")}
                        </Button>
                    </Box>
                </Box>
                <TableFooter
                    className={style.footer}
                    rightColumn={
                        <Pagination
                            maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                            totalPages={serverPage.totalPages}
                            currentPage={currentPage}
                            handlePageChange={(page) => changeView(page, searchQuery)}
                            paginationText={t("DeviceStatus.showingRules", {
                                start: pageStartIndex + (savedRowCountOnPage > 0 ? 1 : 0),
                                end: pageStartIndex + savedRowCountOnPage,
                                total: serverPage.totalRules,
                            })}
                        />
                    }
                />
            </SubPageLayout>
            <ImportDeviceValueRulesModal
                isOpen={isImportModalVisible}
                onClose={() => setIsImportModalVisible(false)}
                onImport={handleImport}
            />
            <Modal
                isOpen={isCancelDialogVisible}
                hideModal={() => setIsCancelDialogVisible(false)}
                title={t("DeviceStatus.cancel.title")}
                width={"Md"}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => setIsCancelDialogVisible(false)}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("DeviceStatus.cancel.keepEditing")}
                        </Button>
                        <Button
                            variant="Secondary"
                            onClick={handleDontSave}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("DeviceStatus.cancel.dontSave")}
                        </Button>
                    </>
                }
            >
                <p>{t("DeviceStatus.cancel.warning")}</p>
            </Modal>
            <Modal
                isOpen={isConfirmSaveVisible}
                hideModal={() => setIsConfirmSaveVisible(false)}
                title={t("DeviceStatus.confirmationDialogForSaving.title")}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => {
                                setIsConfirmSaveVisible(false);
                                handleSave();
                            }}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("Common.save")}
                        </Button>
                        <Button
                            variant="Secondary"
                            onClick={() => setIsConfirmSaveVisible(false)}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("Common.cancel")}
                        </Button>
                    </>
                }
            >
                <TextBlock disableBottomSpacing>{t("DeviceStatus.confirmationDialogForSaving.message")}</TextBlock>
                <RuleChangeList
                    title={t("DeviceStatus.confirmationDialogForSaving.newlyCreated")}
                    rules={pendingRulesByAction("CREATE")}
                />
                <RuleChangeList
                    title={t("DeviceStatus.confirmationDialogForSaving.editedRules")}
                    rules={pendingRulesByAction("EDIT")}
                />
                <RuleChangeList
                    title={t("DeviceStatus.confirmationDialogForSaving.deletedRules")}
                    rules={pendingRulesByAction("DELETE")}
                />
            </Modal>
            <AlertModal
                isOpen={isErrorModalVisible}
                hideModal={() => {
                    setIsErrorModalVisible(false);
                    discardChanges();
                    setBatchError(null);
                }}
                title={t("Common.error")}
                variant="Error"
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => {
                            setIsErrorModalVisible(false);
                            discardChanges();
                            setBatchError(null);
                        }}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.close")}
                    </Button>
                }
            >
                <p>{batchError}</p>
            </AlertModal>
        </>
    );
};

const DeviceValueView = () => {
    const { t } = useTranslation();
    return (
        <ApplicationLayout
            mainRoute={RULES_ROUTE}
            viewTitle={t("DeviceStatus.viewTitle")}
            compactViewTitle
            view={<DeviceStatusContent />}
        />
    );
};

export default DeviceValueView;
